import { useState } from 'react';
import { passwordSchema } from '@colette/shared';
import { C, DEGRADE_ACTION } from '../../theme';
import { styleChamp } from './champs';
import { api } from '../../api/routes';
import { messageDErreur } from '../../api/useDonnees';

/**
 * Changer son mot de passe (2026-08-28).
 *
 * `POST /auth/mot-de-passe` existait depuis le jalon 1 et n'avait JAMAIS eu d'ecran :
 * ecrit, teste, et inaccessible — exactement comme l'etait la correction des
 * propositions avant le lot D. Une testeuse l'a signale en cherchant a changer le sien.
 *
 * Ce n'est PAS la reinitialisation d'un mot de passe oublie : il faut connaitre l'ancien.
 * Un adulte qui a perdu le sien reste enferme dehors — c'est un chantier a part, qui
 * demande d'envoyer des emails.
 */
export function MotDePasse({ signaler }: { signaler: (message: string) => void }) {
  const [ouvert, setOuvert] = useState(false);
  const [actuel, setActuel] = useState('');
  const [nouveau, setNouveau] = useState('');
  const [envoi, setEnvoi] = useState(false);

  const verifie = passwordSchema.safeParse(nouveau);
  // On ne montre la regle que quand quelque chose est deja tape : l'afficher a vide
  // ressemblerait a un reproche avant meme d'avoir commence.
  const faute = nouveau !== '' && !verifie.success ? verifie.error.issues[0]!.message : null;
  const peutEnvoyer = actuel !== '' && verifie.success && !envoi;

  const enregistrer = async () => {
    if (!peutEnvoyer) return;
    setEnvoi(true);
    try {
      await api.changerMotDePasse(actuel, nouveau);
      setActuel('');
      setNouveau('');
      setOuvert(false);
      signaler('Mot de passe changé');
    } catch (cause) {
      signaler(messageDErreur(cause));
    } finally {
      setEnvoi(false);
    }
  };

  if (!ouvert) {
    return (
      <div
        style={{
          background: C.carte,
          border: `1px solid ${C.bordure}`,
          borderRadius: 14,
          padding: '13px 16px',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
        }}
      >
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: C.encre }}>Mot de passe</div>
          <div style={{ fontSize: 12, color: C.secondaire }}>
            Il faut connaître l’actuel pour en choisir un nouveau.
          </div>
        </div>
        <button
          type="button"
          className="pressable"
          aria-label="Changer mon mot de passe"
          onClick={() => setOuvert(true)}
          style={{
            border: `1px solid ${C.violetBordure}`,
            background: C.carte,
            color: C.violetTexte,
            borderRadius: 10,
            padding: '7px 12px',
            fontSize: 13,
            fontWeight: 800,
            fontFamily: 'inherit',
            cursor: 'pointer',
            flexShrink: 0,
          }}
        >
          Changer
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void enregistrer();
      }}
      style={{
        background: C.carte,
        border: `1px solid ${C.violetBordure}`,
        borderRadius: 14,
        padding: '14px 16px',
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
      }}
    >
      <Ligne libelle="Mon mot de passe actuel">
        <input
          type="password"
          value={actuel}
          onChange={(e) => setActuel(e.target.value)}
          aria-label="Mon mot de passe actuel"
          autoComplete="current-password"
          autoFocus
          style={styleChamp}
        />
      </Ligne>
      <Ligne libelle="Mon nouveau mot de passe">
        <input
          type="password"
          value={nouveau}
          onChange={(e) => setNouveau(e.target.value)}
          aria-label="Mon nouveau mot de passe"
          autoComplete="new-password"
          aria-invalid={faute !== null || undefined}
          style={styleChamp}
        />
      </Ligne>
      {faute !== null ? (
        <div style={{ fontSize: 12.5, fontWeight: 700, color: C.corailTexte }}>{faute}</div>
      ) : null}

      {/* Dit AVANT le geste, pas apres : le serveur revoque les autres sessions, et
          decouvrir sa tablette deconnectee sans explication serait une mauvaise surprise. */}
      <div style={{ fontSize: 12, color: C.secondaire, lineHeight: 1.45 }}>
        Tes autres appareils seront déconnectés. Celui-ci reste ouvert.
      </div>

      <div style={{ display: 'flex', gap: 8 }}>
        <button
          type="submit"
          className="pressable"
          disabled={!peutEnvoyer}
          style={{
            flex: 1,
            height: 44,
            border: 'none',
            borderRadius: 12,
            background: peutEnvoyer ? DEGRADE_ACTION : C.inactif,
            color: '#FFFFFF',
            fontSize: 14,
            fontWeight: 800,
            fontFamily: 'inherit',
            cursor: peutEnvoyer ? 'pointer' : 'default',
          }}
        >
          {envoi ? '…' : 'Enregistrer'}
        </button>
        <button
          type="button"
          className="pressable"
          onClick={() => {
            setActuel('');
            setNouveau('');
            setOuvert(false);
          }}
          style={{
            height: 44,
            padding: '0 14px',
            border: `1px solid ${C.bordure}`,
            borderRadius: 12,
            background: C.carte,
            color: C.secondaire,
            fontSize: 14,
            fontWeight: 700,
            fontFamily: 'inherit',
            cursor: 'pointer',
          }}
        >
          Annuler
        </button>
      </div>
    </form>
  );
}

function Ligne({ libelle, children }: { libelle: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={{ fontSize: 11.5, fontWeight: 800, letterSpacing: 0.6, color: C.tertiaire }}>
        {libelle.toUpperCase()}
      </span>
      {children}
    </label>
  );
}
